import React, { useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Container, Typography, Box, Grid, CircularProgress, Pagination } from '@mui/material';
import { fetchNews } from '../api';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(8),
  },
  newsItem: {
    marginBottom: theme.spacing(6),
  },
  imageContainer: {
    width: '100%',
    borderRadius: '12px',
    overflow: 'hidden',
    height: '240px',
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
      marginBottom: theme.spacing(2),
    },
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  date: {
    fontSize: '0.875rem',
    color: '#64748B',
    marginBottom: theme.spacing(1),
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1E293B',
    lineHeight: 1.4,
    marginBottom: theme.spacing(2),
  },
  description: {
    fontSize: '1rem',
    color: '#475569',
    lineHeight: 1.6,
    marginBottom: theme.spacing(2),
  },
  link: {
    fontSize: '0.875rem',
    color: '#4361EE',
    textDecoration: 'none',
    wordBreak: 'break-all',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(4),
    '& .MuiPaginationItem-root': {
      color: '#64748B',
      '&.Mui-selected': {
        backgroundColor: '#4361EE',
        color: 'white',
        '&:hover': {
          backgroundColor: '#3651D4',
        },
      },
    },
  },
}));

const PressNewsList = () => {
  const classes = useStyles();
  const [newsData, setNewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const loadNews = async () => {
      try {
        const data = await fetchNews();
        // Sort news by date (newest first)
        const sortedData = data.sort((a, b) => new Date(b.date) - new Date(a.date));
        setNewsData(sortedData);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    loadNews();
  }, []);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (newsData.length === 0) return null;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = newsData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(newsData.length / itemsPerPage);

  return (
    <Container maxWidth="lg" className={classes.root}>
      {currentItems.map((item) => (
        <Grid container spacing={4} key={item.id} className={classes.newsItem}>
          <Grid item xs={12} md={5}>
            <Box className={classes.imageContainer}>
              <img src={item.imageUrl} alt={item.title} className={classes.image} />
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box className={classes.contentContainer}>
              <Typography className={classes.date}>
                {item.date ? moment(item.date).format('MMMM D, YYYY') : ''}
              </Typography>
              <Typography variant="h3" className={classes.title}>
                {item.title}
              </Typography>
              <Typography className={classes.description}>
                {item.description}
              </Typography>
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.link}
              >
                {item.link}
              </a>
            </Box>
          </Grid>
        </Grid>
      ))}

      {totalPages > 1 && (
        <Box className={classes.paginationContainer}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            shape="rounded"
            color="primary"
          />
        </Box>
      )}
    </Container>
  );
};

export default PressNewsList;
